/*
 * component-cart-bundle-upsell.js - <cart-bundle-upsell> Custom Element.
 *
 * Wraps the bundle progress card in both the cart drawer and the PDP.
 *
 * Responsibilities:
 *   - Read initial eligible-line state from data attributes (server-rendered
 *     by snippets/cart-bundle-upsell.liquid).
 *   - Re-render the markup tree on every cart:updated event, reading the live
 *     cart state from event.detail.cart.
 *   - Render one of three branches: drawer-empty (hidden), regular-upsell,
 *     max-state celebration.
 *   - Fire confetti once per session on the first transition into the max tier.
 *
 * Communication:
 *   Listens: cart:updated (document).
 *   Dispatches: none. The drawer's delegated click handler picks up
 *     data-action="bump-line" to bump the first eligible line quantity.
 *
 * Source: BUSINESS_RULES.md "Bundle Progress Messaging"; spec
 * .claude/specs/2026-06-26-phase-8-bundle-engine-design.md Section 8.
 */

(function () {
  if (window.customElements && window.customElements.get('cart-bundle-upsell')) return;

  // Mirrors BUSINESS_RULES.md > Bundle Discount Rules and the 6 native
  // automatic discounts in Shopify admin (titles `Bundle discount: N% off`).
  // Native discounts are authoritative for the actual discount applied.
  var TIERS = [
    { qty: 2, pct: 5  },
    { qty: 3, pct: 10 },
    { qty: 4, pct: 15 },
    { qty: 5, pct: 20 },
    { qty: 6, pct: 25 },
    { qty: 7, pct: 30 }
  ];

  function nextTier(qty) {
    for (var i = 0; i < TIERS.length; i++) {
      if (qty < TIERS[i].qty) return TIERS[i];
    }
    return null;
  }

  function isMaxed(qty) {
    return qty >= TIERS[TIERS.length - 1].qty;
  }

  function escape(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  function escapeAttr(s) {
    return escape(s);
  }

  // Minimal inline SVG for the max-state icon. The icon sprite at
  // assets/icons.svg does not include a party-popper symbol and the CE has no
  // access to the asset URL, so we inline a small celebration glyph.
  function maxIconSvg() {
    return (
      '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<path d="M5.8 11.3 2 22l10.7-3.79"></path>' +
        '<path d="M4 3h.01"></path>' +
        '<path d="M22 8h.01"></path>' +
        '<path d="M15 2h.01"></path>' +
        '<path d="M22 20h.01"></path>' +
        '<path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10"></path>' +
        '<path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17"></path>' +
        '<path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7"></path>' +
        '<path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z"></path>' +
      '</svg>'
    );
  }

  function template(opts) {
    var ctx = opts.context;
    var qty = opts.eligibleQty;
    var maxed = isMaxed(qty);
    var nt = nextTier(qty);
    var pct = nt ? nt.pct : 30;
    var L = (window.LunchedBundleCopy || {});

    if (ctx === 'drawer' && qty === 0) {
      return '';  // drawer with no eligible items: hidden by host.
    }

    // Locale strings come from window.LunchedBundleCopy, which is sourced from
    // Liquid's `| t | json` and is already HTML-safe. We embed them directly
    // without re-escaping. Untrusted data (product titles, image URLs, line
    // keys) is still escaped via escape() / escapeAttr().
    if (maxed) {
      var maxPct = TIERS[TIERS.length - 1].pct;
      var maxText = (L.max_unlocked || 'Maximum discount unlocked: __PCT__% OFF').replace('__PCT__', String(maxPct));
      return (
        '<div class="cart-bundle-upsell__max" role="status">' +
          '<span class="cart-bundle-upsell__max-icon" aria-hidden="true">' +
            maxIconSvg() +
          '</span>' +
          '<span class="cart-bundle-upsell__max-text">' + maxText + '</span>' +
          '<span class="cart-bundle-upsell__confetti" aria-hidden="true"></span>' +
        '</div>'
      );
    }

    if (qty === 0) {
      // pdp + 0 eligible: empty-state "start saving" card.
      var startCopy = L.start_saving || 'Add a lunch box to start saving';
      var emptyCta  = L.upsell_cta   || 'Add';
      return (
        '<div class="cart-bundle-upsell__card cart-bundle-upsell__card--empty">' +
          '<div class="cart-bundle-upsell__body">' +
            '<span class="cart-bundle-upsell__eyebrow">' + startCopy + '</span>' +
          '</div>' +
          '<button type="button" class="cart-bundle-upsell__cta" data-action="scroll-to-picker">' +
            emptyCta +
          '</button>' +
        '</div>'
      );
    }

    // 1-6 eligible boxes: "ONE MORE, X% OFF" card.
    var eyebrow = (L.upsell_one_more_eyebrow || 'ONE MORE, __PCT__% OFF').replace('__PCT__', String(pct));
    var body    = (L.upsell_one_more         || "Add another to your bag - we'll knock __PCT__% off it.").replace('__PCT__', String(pct));
    var first   = opts.firstLine || null;
    var title   = (first && first.product_title) || opts.firstLineTitle || '';
    var img     = (first && first.featured_image && first.featured_image.url) || opts.firstLineImg || '';
    var key     = (first && first.key) || opts.firstLineKey || '';
    var ctaLabel = L.upsell_cta || 'Add';

    var ctaButton;
    if (ctx === 'drawer') {
      ctaButton = (
        '<button type="button" class="cart-bundle-upsell__cta" ' +
          'data-action="bump-line" data-line-key="' + escapeAttr(key) + '">' +
          ctaLabel +
        '</button>'
      );
    } else {
      // PDP: scroll to the variant picker.
      ctaButton = (
        '<button type="button" class="cart-bundle-upsell__cta" data-action="scroll-to-picker">' +
          ctaLabel +
        '</button>'
      );
    }

    return (
      '<div class="cart-bundle-upsell__card">' +
        (img ? '<img class="cart-bundle-upsell__img" src="' + escapeAttr(img) + '" alt="" loading="lazy" width="56" height="56">' : '') +
        '<div class="cart-bundle-upsell__body">' +
          '<span class="cart-bundle-upsell__eyebrow">' + eyebrow + '</span>' +
          (title ? '<span class="cart-bundle-upsell__title">' + escape(title) + '</span>' : '') +
          '<span class="cart-bundle-upsell__sub">' + body + '</span>' +
        '</div>' +
        ctaButton +
      '</div>'
    );
  }

  class CartBundleUpsell extends HTMLElement {
    constructor() {
      super();
      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this._context = this.dataset.context || 'drawer';
      this._lastMaxed = false;
      this._render({
        context: this._context,
        eligibleQty: parseInt(this.dataset.eligibleQty, 10) || 0,
        firstLine: null,
        firstLineKey: this.dataset.firstLineKey || '',
        firstLineTitle: this.dataset.firstLineTitle || '',
        firstLineImg: this.dataset.firstLineImg || ''
      });

      document.addEventListener('cart:updated', this._onCartUpdated);
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onCartUpdated);
      this.removeEventListener('click', this._onClick);
    }

    _onCartUpdated(e) {
      // The AJAX /cart.js response does NOT include `cart.items[].product.metafields`,
      // so we cannot derive eligibility from the cart object. Instead, re-read from
      // this.dataset which the SRA swap updates with fresh server-rendered values
      // (Liquid has full metafield access).
      //
      // Drawer context: SRA swap replaces the entire drawer DOM, which means the
      // OLD CE instance is disconnected and a NEW CE instance with fresh dataset
      // is connected. The cart:updated event then fires on the new instance, and
      // we re-render from its (already fresh) dataset - idempotent.
      //
      // PDP context: no SRA swap; dataset stays at page-load values. Live cart
      // changes don't reflect in the PDP card until the page is reloaded. This
      // is an accepted limitation for the sticky PDP card.
      if (!e || !e.detail) return;
      this._render({
        context: this._context,
        eligibleQty: parseInt(this.dataset.eligibleQty, 10) || 0,
        firstLineKey: this.dataset.firstLineKey || '',
        firstLineTitle: this.dataset.firstLineTitle || '',
        firstLineImg: this.dataset.firstLineImg || ''
      });
    }

    _onClick(e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var action = btn.dataset.action;
      if (action === 'scroll-to-picker') {
        var picker = document.querySelector('variant-picker') ||
                     document.getElementById('variant-picker') ||
                     document.querySelector('[data-variant-picker]');
        if (picker && picker.scrollIntoView) {
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        e.preventDefault();
      }
      // 'bump-line' is handled by the drawer's delegated click handler; do
      // not preventDefault here.
    }

    _render(opts) {
      var wasMaxed = this._lastMaxed === true;
      var isNowMaxed = isMaxed(opts.eligibleQty);

      var html = template(opts);
      this.innerHTML = html;

      if (opts.context === 'drawer' && opts.eligibleQty === 0) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }

      // One-shot confetti on the first transition into the max tier per session.
      if (isNowMaxed && !wasMaxed && !sessionStorage.getItem('bundle:max-celebrated')) {
        this._fireConfetti();
        try { sessionStorage.setItem('bundle:max-celebrated', '1'); } catch (_) { /* ignore */ }
      }
      this._lastMaxed = isNowMaxed;
    }

    _fireConfetti() {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var el = this.querySelector('.cart-bundle-upsell__confetti');
      if (!el) return;
      el.classList.add('is-firing');
      setTimeout(function () { el.classList.remove('is-firing'); }, 1900);
    }
  }

  customElements.define('cart-bundle-upsell', CartBundleUpsell);
})();
